import { useCallback, useEffect, useState, type RefObject } from 'react';

/** Sub-pixel slack so rounding never reports a phantom hidden column. */
const EDGE_TOLERANCE_PX = 1;

export interface HorizontalOverflowState {
  /** Header columns cut off (fully or partly) past the right edge. */
  hiddenCount: number;
  /** Width of the container's vertical scrollbar (0 for overlay scrollbars). */
  scrollbarWidth: number;
  /** Height of the container's horizontal scrollbar (0 for overlay scrollbars). */
  scrollbarHeight: number;
}

const NO_OVERFLOW: HorizontalOverflowState = {
  hiddenCount: 0,
  scrollbarWidth: 0,
  scrollbarHeight: 0,
};

const sameState = (
  a: HorizontalOverflowState,
  b: HorizontalOverflowState
): boolean =>
  a.hiddenCount === b.hiddenCount &&
  a.scrollbarWidth === b.scrollbarWidth &&
  a.scrollbarHeight === b.scrollbarHeight;

/**
 * Measure how many table columns sit past the scroll container's right edge.
 * Re-measured on scroll and whenever the container or table resizes, plus
 * whenever `recomputeKey` changes (e.g. the column set or order).
 */
export function useHorizontalOverflow(
  containerRef: RefObject<HTMLElement | null>,
  recomputeKey: string
): HorizontalOverflowState {
  const [overflow, setOverflow] = useState(NO_OVERFLOW);
  const update = useCallback((next: HorizontalOverflowState): void => {
    setOverflow(previous => (sameState(previous, next) ? previous : next));
  }, []);

  const measure = useCallback((): void => {
    const container = containerRef.current;
    if (!container) return;
    const atEnd =
      container.scrollLeft + container.clientWidth >=
      container.scrollWidth - EDGE_TOLERANCE_PX;
    if (atEnd) {
      update(NO_OVERFLOW);
      return;
    }
    // The visible content box ends at clientLeft + clientWidth — excluding a
    // classic (non-overlay) vertical scrollbar, which would otherwise hide a
    // column's edge without it being counted.
    const visibleRight =
      container.getBoundingClientRect().left +
      container.clientLeft +
      container.clientWidth +
      EDGE_TOLERANCE_PX;
    const headerCells = container.querySelectorAll<HTMLElement>(
      'thead tr:last-child > th'
    );
    let count = 0;
    headerCells.forEach(cell => {
      if (cell.getBoundingClientRect().right > visibleRight) count += 1;
    });
    update({
      hiddenCount: count,
      scrollbarWidth: Math.max(
        container.offsetWidth -
          container.clientWidth -
          2 * container.clientLeft,
        0
      ),
      scrollbarHeight: Math.max(
        container.offsetHeight -
          container.clientHeight -
          2 * container.clientTop,
        0
      ),
    });
  }, [containerRef, update]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;
    // First measurement after layout (not synchronously in the effect).
    const frame = requestAnimationFrame(measure);
    container.addEventListener('scroll', measure, { passive: true });
    window.addEventListener('resize', measure);
    const observer =
      typeof ResizeObserver === 'undefined'
        ? null
        : new ResizeObserver(measure);
    observer?.observe(container);
    const table = container.querySelector('table');
    if (table) observer?.observe(table);
    return () => {
      cancelAnimationFrame(frame);
      container.removeEventListener('scroll', measure);
      window.removeEventListener('resize', measure);
      observer?.disconnect();
    };
  }, [containerRef, measure, recomputeKey]);

  return overflow;
}
